import { z } from 'zod';

// User roles hierarchy
// - super_admin: only for master tenant (system-wide control)
// - admin:      tenant administrator (full control within tenant)
// - manager:    limited admin permissions
// - user/agent: regular users
export const UserRoleSchema = z.enum([
  'super_admin',    // Master tenant only (system administrator)
  'admin',          // Tenant administrator - manages everything within their tenant
  'manager',        // Manager with limited admin permissions
  'user',           // Regular user - limited access within tenant
  'agent',          // Agent (call-center / service operator)
]);

export type UserRole = z.infer<typeof UserRoleSchema>;

// User permissions based on role and is_master_tenant flag
export const UserPermissionsSchema = z.object({
  // Master Tenant Admin permissions (when is_master_tenant: true)
  can_create_tenants: z.boolean().default(false),
  can_delete_tenants: z.boolean().default(false),
  can_manage_system_settings: z.boolean().default(false),
  can_view_all_tenants: z.boolean().default(false),
  
  // Tenant Admin permissions
  can_manage_tenant_settings: z.boolean().default(false),
  can_create_stores: z.boolean().default(false),
  can_manage_stores: z.boolean().default(false),
  can_create_trunks: z.boolean().default(false),
  can_manage_trunks: z.boolean().default(false),
  can_create_extensions: z.boolean().default(false),
  can_manage_extensions: z.boolean().default(false),
  can_view_cdr: z.boolean().default(false),
  can_manage_users: z.boolean().default(false),
  
  // Tenant User permissions
  can_view_own_extensions: z.boolean().default(false),
  can_make_calls: z.boolean().default(false),
  can_view_own_cdr: z.boolean().default(false),
  can_manage_own_settings: z.boolean().default(false)
});

export type UserPermissions = z.infer<typeof UserPermissionsSchema>;

// User schema
export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string().min(1).max(100),
  role: UserRoleSchema,
  tenant_id: z.string().uuid(), // All users belong to a tenant (including master tenant)
  status: z.enum(['active', 'inactive', 'suspended']),
  created_at: z.date(),
  updated_at: z.date(),
  last_login: z.date().optional(),
  permissions: UserPermissionsSchema
});

export type User = z.infer<typeof UserSchema>;

// Helper function to get permissions based on role and is_master_tenant flag
export function getPermissionsForRole(role: UserRole, isMasterTenant: boolean = false): UserPermissions {
  switch (role) {
    case 'super_admin':
      return {
        can_create_tenants: true,
        can_delete_tenants: true,
        can_manage_system_settings: true,
        can_view_all_tenants: true,
        can_manage_tenant_settings: true,
        can_create_stores: true,
        can_manage_stores: true,
        can_create_trunks: true,
        can_manage_trunks: true,
        can_create_extensions: true,
        can_manage_extensions: true,
        can_view_cdr: true,
        can_manage_users: true,
        can_view_own_extensions: true,
        can_make_calls: true,
        can_view_own_cdr: true,
        can_manage_own_settings: true
      };

    case 'admin':
      return {
        can_create_tenants: isMasterTenant,
        can_delete_tenants: isMasterTenant,
        can_manage_system_settings: isMasterTenant,
        can_view_all_tenants: isMasterTenant,
        can_manage_tenant_settings: true,
        can_create_stores: true,
        can_manage_stores: true,
        can_create_trunks: true,
        can_manage_trunks: true,
        can_create_extensions: true,
        can_manage_extensions: true,
        can_view_cdr: true,
        can_manage_users: true,
        can_view_own_extensions: true,
        can_make_calls: true,
        can_view_own_cdr: true,
        can_manage_own_settings: true
      };
    
    case 'manager':
      return {
        can_create_tenants: false,
        can_delete_tenants: false,
        can_manage_system_settings: false,
        can_view_all_tenants: false,
        can_manage_tenant_settings: false,
        can_create_stores: false,
        can_manage_stores: false,
        can_create_trunks: false,
        can_manage_trunks: false,
        can_create_extensions: false,
        can_manage_extensions: true,
        can_view_cdr: true,
        can_manage_users: false,
        can_view_own_extensions: true,
        can_make_calls: true,
        can_view_own_cdr: true,
        can_manage_own_settings: true
      };
    
    case 'user':
    case 'agent':
      return {
        can_create_tenants: false,
        can_delete_tenants: false,
        can_manage_system_settings: false,
        can_view_all_tenants: false,
        can_manage_tenant_settings: false,
        can_create_stores: false,
        can_manage_stores: false,
        can_create_trunks: false,
        can_manage_trunks: false,
        can_create_extensions: false,
        can_manage_extensions: false,
        can_view_cdr: false,
        can_manage_users: false,
        can_view_own_extensions: true,
        can_make_calls: true,
        can_view_own_cdr: true,
        can_manage_own_settings: true
      };
    
    default:
      return {
        can_create_tenants: false,
        can_delete_tenants: false,
        can_manage_system_settings: false,
        can_view_all_tenants: false,
        can_manage_tenant_settings: false,
        can_create_stores: false,
        can_manage_stores: false,
        can_create_trunks: false,
        can_manage_trunks: false,
        can_create_extensions: false,
        can_manage_extensions: false,
        can_view_cdr: false,
        can_manage_users: false,
        can_view_own_extensions: false,
        can_make_calls: false,
        can_view_own_cdr: false,
        can_manage_own_settings: false
      };
  }
}

